import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  QrCode,
  Smartphone,
  Unlink,
  Upload,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ClickToCopy } from '@/components/ui/click-to-copy';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  FONTE_OTP_GERENCIADO,
  TOTP_STATUS_QUERY_KEY,
  exportarOtpQrCode,
  fetchQrCodeTotpStatus,
  gerarCodigoOtp,
  provisionarOtp,
  revogarOtp,
} from '@/api/certificados';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useNotification } from '@/context/NotificationProvider';

import { QrCodeTotpUploader } from './QrCodeTotpUploader';

/** Código do autenticador quando o bootstrap exige o OTP atual do usuário. */
const CODIGO_OTP_NECESSARIO = 'TOTP_SECRET_INDISPONIVEL';

type MetodoConexao = 'codigo' | 'imagem';

const METODOS: { id: MetodoConexao; label: string; icon: React.ReactNode }[] = [
  { id: 'codigo', label: 'Código', icon: <KeyRound className="h-3.5 w-3.5" /> },
  { id: 'imagem', label: 'Imagem', icon: <QrCode className="h-3.5 w-3.5" /> },
];

interface ConectarPdpjCardProps {
  /** Reduz descrições para uso em telas com pouco espaço (onboarding). */
  compact?: boolean;
}

/**
 * Card "Conectar-se à PDPJ" — único ponto de entrada para habilitar assinatura
 * e consulta automáticas.
 *
 * "Conectar-se à PDPJ" é a *necessidade*; existem dois *modos* de fazê-lo, e
 * este card os unifica numa só experiência:
 * - **Recomendado (gerenciado):** o JurisApp cadastra um segundo fator OTP
 *   adicional na conta da PDPJ (mantendo o OTP pessoal intacto), captura o
 *   segredo e passa a gerar os códigos por ele — inclusive offline. Pede uma
 *   única vez o código atual do app do usuário (bootstrap).
 * - **Alternativa (discreta):** compartilhar o QR Code de um autenticador já
 *   existente, revelado por divulgação progressiva ({@link QrCodeTotpUploader}).
 *
 * Estados: carregando · acabou-de-conectar (mostra QR do segredo) · conectado
 * por OTP gerenciado (status + desconectar) · conectado por QR compartilhado
 * (status + reenviar) · não conectado (modo recomendado + alternativa).
 */
export function ConectarPdpjCard({ compact = false }: ConectarPdpjCardProps) {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: TOTP_STATUS_QUERY_KEY,
    queryFn: fetchQrCodeTotpStatus,
    staleTime: 30_000,
  });

  const codigoInputRef = useRef<HTMLInputElement>(null);
  const [codigo, setCodigo] = useState('');
  const [mostrarCodigo, setMostrarCodigo] = useState(false);
  const [erroCampoCodigo, setErroCampoCodigo] = useState<string | null>(null);
  const [conectando, setConectando] = useState(false);
  const [otpauthRecemCriado, setOtpauthRecemCriado] = useState<string | null>(null);
  const [confirmarDesconexao, setConfirmarDesconexao] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [metodo, setMetodo] = useState<MetodoConexao>('codigo');
  const [mostrarQrDialog, setMostrarQrDialog] = useState(false);
  const [otpauthQr, setOtpauthQr] = useState<string | null>(null);
  const [carregandoQr, setCarregandoQr] = useState(false);
  const [mostrarCodigoDialog, setMostrarCodigoDialog] = useState(false);
  const [codigoOtp, setCodigoOtp] = useState<{ codigo: string; validoAte: string | null } | null>(null);
  const [carregandoCodigo, setCarregandoCodigo] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [reenviarQr, setReenviarQr] = useState(false);

  const pareado = statusQuery.data?.pareado ?? false;
  const fonte = statusQuery.data?.fonte ?? null;
  const conectadoGerenciado = pareado && fonte === FONTE_OTP_GERENCIADO;
  const codigoLoginDireto = statusQuery.data?.codigo_login_direto ?? false;

  // Contagem regressiva do código (renova-se a cada janela TOTP). Recalcula do
  // instante de expiração a cada tick para não acumular drift.
  useEffect(() => {
    if (!mostrarCodigoDialog || !codigoOtp?.validoAte) return;
    const alvo = new Date(codigoOtp.validoAte).getTime();
    const tick = () =>
      setSegundosRestantes(Math.max(0, Math.ceil((alvo - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mostrarCodigoDialog, codigoOtp]);

  const handleConectar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErroCampoCodigo(null);
    try {
      setConectando(true);
      const data = await provisionarOtp(codigo.trim() || undefined);
      setCodigo('');
      setOtpauthRecemCriado(data.otpauth_uri || null);
      await queryClient.invalidateQueries({ queryKey: TOTP_STATUS_QUERY_KEY });
      showSuccess('Conectado à PDPJ com sucesso.');
    } catch (err) {
      // Cenário B do mapa: a PDPJ exigiu o código atual e ele veio em branco.
      // É recuperável no próprio campo — mostra inline + foca, sem toast.
      const classified = classifyError(err);
      if (classified.codigo === CODIGO_OTP_NECESSARIO) {
        setErroCampoCodigo(classified.detail || classified.message);
        codigoInputRef.current?.focus();
      } else {
        notifyMutationError(err, showError);
      }
    } finally {
      setConectando(false);
    }
  };

  const handleDesconectar = async () => {
    try {
      setDesconectando(true);
      await revogarOtp();
      setConfirmarDesconexao(false);
      // Segredo em memória deixou de valer (credencial revogada na PDPJ).
      setOtpauthRecemCriado(null);
      setOtpauthQr(null);
      await queryClient.invalidateQueries({ queryKey: TOTP_STATUS_QUERY_KEY });
      showSuccess('Desconectado da PDPJ.');
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setDesconectando(false);
    }
  };

  // Abre o dialog do QR. Reaproveita o segredo em memória (logo após conectar)
  // ou o busca sob demanda no autenticador (sessões posteriores).
  const handleMostrarQr = async () => {
    setMostrarQrDialog(true);
    if (otpauthRecemCriado) {
      setOtpauthQr(otpauthRecemCriado);
      return;
    }
    if (otpauthQr) return;
    try {
      setCarregandoQr(true);
      const data = await exportarOtpQrCode();
      setOtpauthQr(data.otpauth_uri || null);
    } catch (err) {
      setMostrarQrDialog(false);
      notifyMutationError(err, showError);
    } finally {
      setCarregandoQr(false);
    }
  };

  // Busca o código TOTP atual no backend (auditado). Reusada no "Gerar novo".
  const buscarCodigo = async () => {
    try {
      setCarregandoCodigo(true);
      const data = await gerarCodigoOtp();
      setCodigoOtp({ codigo: data.codigo, validoAte: data.valido_ate });
    } catch (err) {
      setMostrarCodigoDialog(false);
      notifyMutationError(err, showError);
    } finally {
      setCarregandoCodigo(false);
    }
  };

  const handleVerCodigo = async () => {
    setCodigoOtp(null);
    setMostrarCodigoDialog(true);
    await buscarCodigo();
  };

  // --- Loading do status ---
  if (statusQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <CardTitle>Autenticação na PDPJ</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="flex items-center justify-center py-8"
            role="status"
            aria-label="Verificando conexão com a PDPJ"
          >
            <Spinner size={32} />
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Conectado por OTP gerenciado: status + desconectar + compartilhar QR ---
  // `otpauthRecemCriado` força este estado logo após conectar, antes de o status
  // refazer o fetch (evita um flash do formulário).
  if (conectadoGerenciado || otpauthRecemCriado) {
    return (
      <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <CardTitle>Conectado à PDPJ</CardTitle>
            </div>
            {!compact && (
              <CardDescription>
                O JurisApp entra na PDPJ por você automaticamente — assinatura e
                consulta funcionam sem ação sua. Todos os acessos ficam no seu
                histórico. Você pode desconectar quando quiser.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {codigoLoginDireto && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={handleVerCodigo}
                >
                  <KeyRound className="h-4 w-4" />
                  Ver código atual
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleMostrarQr}
              >
                <QrCode className="h-4 w-4" />
                Compartilhar QR Code
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setConfirmarDesconexao(true)}
              >
                <Unlink className="h-4 w-4" />
                Desconectar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dialog do QR — adicionar o mesmo código ao app pessoal (opcional) */}
        <Dialog
          open={mostrarQrDialog}
          onOpenChange={(open: boolean) => {
            setMostrarQrDialog(open);
            if (!open) setOtpauthQr(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar ao seu aplicativo autenticador</DialogTitle>
              <DialogDescription>
                Escaneie o QR Code abaixo para ter o mesmo código também no seu
                celular (opcional). Seu autenticador atual continua funcionando.
              </DialogDescription>
            </DialogHeader>
            {carregandoQr ? (
              <div
                className="flex items-center justify-center py-10"
                role="status"
                aria-label="Carregando QR Code"
              >
                <Spinner size={32} />
              </div>
            ) : otpauthQr ? (
              <div className="flex flex-col items-center gap-3 py-2">
                <div className="rounded-lg border border-border bg-white p-4">
                  <QRCodeSVG value={otpauthQr} size={184} level="M" />
                </div>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Smartphone className="h-3.5 w-3.5" />
                  Escaneie com Google Authenticator, FreeOTP ou similar.
                </p>
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" onClick={() => setMostrarQrDialog(false)}>
                Concluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog do código atual — login direto na PDPJ (caso "só JurisApp") */}
        <Dialog
          open={mostrarCodigoDialog}
          onOpenChange={(open: boolean) => {
            setMostrarCodigoDialog(open);
            if (!open) setCodigoOtp(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Código para entrar na PDPJ</DialogTitle>
              <DialogDescription>
                Use este código de 6 dígitos para entrar direto no portal da PDPJ.
                Ele muda a cada 30 segundos.
              </DialogDescription>
            </DialogHeader>
            {carregandoCodigo ? (
              <div
                className="flex items-center justify-center py-10"
                role="status"
                aria-label="Gerando código"
              >
                <Spinner size={32} />
              </div>
            ) : codigoOtp ? (
              <div className="flex flex-col items-center gap-3 py-2">
                {/* Clicar no código (ou no ícone) copia e mostra o toast. */}
                <ClickToCopy
                  text={codigoOtp.codigo}
                  tooltipLabel="Copiar código"
                  className="flex items-center gap-2 rounded-md px-3 py-1.5"
                  display={
                    <>
                      <span
                        className="font-mono text-4xl font-bold tracking-[0.25em]"
                        aria-label={`Código ${codigoOtp.codigo.split('').join(' ')}`}
                      >
                        {codigoOtp.codigo.slice(0, 3)} {codigoOtp.codigo.slice(3)}
                      </span>
                      <Copy className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </>
                  }
                />
                {codigoOtp.validoAte ? (
                  segundosRestantes > 0 ? (
                    <p className="text-xs text-muted-foreground" role="status">
                      Expira em {segundosRestantes}s
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={buscarCodigo}
                    >
                      Gerar novo código
                    </Button>
                  )
                ) : null}
              </div>
            ) : null}
            <DialogFooter>
              <Button type="button" onClick={() => setMostrarCodigoDialog(false)}>
                Concluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={confirmarDesconexao}
          onOpenChange={(open: boolean) => !desconectando && setConfirmarDesconexao(open)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Desconectar da PDPJ</DialogTitle>
              <DialogDescription>
                O JurisApp deixará de entrar na PDPJ por você — assinatura e
                consulta automáticas serão interrompidas até você reconectar. O
                OTP do seu aplicativo pessoal não é afetado.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmarDesconexao(false)}
                disabled={desconectando}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDesconectar}
                disabled={desconectando}
                className="gap-2"
              >
                {desconectando ? (
                  <>
                    <Spinner size={16} className="text-inherit" />
                    Desconectando...
                  </>
                ) : (
                  'Desconectar'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // --- Conectado por QR compartilhado (modo alternativo): status + reenviar ---
  if (pareado) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <CardTitle>Conectado à PDPJ</CardTitle>
          </div>
          {!compact && (
            <CardDescription>
              Recebemos o QR Code do seu aplicativo autenticador. Assinatura e
              consulta de processos já funcionam em seu nome.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {reenviarQr ? (
            <div className="space-y-3">
              <QrCodeTotpUploader compact={compact} onSuccess={() => setReenviarQr(false)} />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReenviarQr(false)}
              >
                Cancelar
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setReenviarQr(true)}
            >
              <Upload className="h-4 w-4" />
              Reenviar QR Code
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // --- Não conectado: modo recomendado (gerenciado) + alternativa discreta ---
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-primary" />
          <CardTitle>Autenticação na PDPJ</CardTitle>
        </div>
        {!compact && (
          <CardDescription>
            Deixe o JurisApp assinar e consultar processos por você, sem
            compartilhar seu autenticador.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {conectando ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-8 text-center"
            role="status"
            aria-label="Conectando à PDPJ"
          >
            <Spinner size={32} />
            <p className="text-sm font-medium">Conectando à PDPJ...</p>
            <p className="text-xs text-muted-foreground">
              Estamos configurando seu acesso com segurança. Isso pode levar
              alguns segundos.
            </p>
          </div>
        ) : (
          <div className={compact ? 'space-y-3' : 'space-y-4'}>
            {/* Escolha única do método (radiogroup): uma parada de Tab só,
                no método marcado; as setas trocam, como num grupo de rádios. */}
            <div
              role="radiogroup"
              aria-label="Como conectar à PDPJ"
              className="flex h-12 items-center gap-0.5 rounded-md border border-input bg-muted/40 p-0.5 sm:h-9"
              onKeyDown={(e) => {
                const passo = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
                if (!passo) return;
                e.preventDefault();
                const atual = METODOS.findIndex((m) => m.id === metodo);
                const proximo = METODOS[(atual + passo + METODOS.length) % METODOS.length];
                setMetodo(proximo.id);
                e.currentTarget
                  .querySelector<HTMLElement>(`[data-metodo="${proximo.id}"]`)
                  ?.focus();
              }}
            >
              {METODOS.map((m) => {
                const selected = metodo === m.id;
                return (
                  <Button
                    key={m.id}
                    type="button"
                    variant="ghost"
                    size="sm"
                    role="radio"
                    aria-checked={selected}
                    tabIndex={selected ? 0 : -1}
                    data-metodo={m.id}
                    onClick={() => setMetodo(m.id)}
                    className={cn(
                      'h-full min-w-0 flex-1 gap-1.5 rounded-[5px] px-2 text-xs sm:text-sm',
                      selected
                        ? 'bg-background font-medium text-foreground shadow-sm hover:bg-background'
                        : 'font-normal text-muted-foreground hover:bg-transparent hover:text-foreground',
                    )}
                  >
                    {m.icon}
                    <span className="truncate">{m.label}</span>
                  </Button>
                );
              })}
            </div>

            {/* (a) Recomendado: o JurisApp cria o próprio OTP na PDPJ. */}
            {metodo === 'codigo' && (
              <form onSubmit={handleConectar} className={compact ? 'space-y-3' : 'space-y-4'}>
                <div className="space-y-1.5">
                  <Label htmlFor="codigo-otp-atual">
                    Código de 6 dígitos do seu app autenticador
                  </Label>
                  <div className="relative">
                    <Input
                      id="codigo-otp-atual"
                      ref={codigoInputRef}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={codigo}
                      onChange={(e) => {
                        setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6));
                        if (erroCampoCodigo) setErroCampoCodigo(null);
                      }}
                      type={mostrarCodigo ? 'text' : 'password'}
                      aria-invalid={erroCampoCodigo ? true : undefined}
                      aria-describedby={erroCampoCodigo ? 'codigo-otp-erro' : undefined}
                      className={[
                        'pr-11 tracking-[0.3em]',
                        erroCampoCodigo ? 'border-destructive focus-visible:ring-destructive' : '',
                      ].join(' ')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setMostrarCodigo((v) => !v)}
                      className="absolute right-0 top-1/2 h-10 w-10 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={mostrarCodigo ? 'Ocultar código' : 'Mostrar código'}
                    >
                      {mostrarCodigo ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {erroCampoCodigo ? (
                    <p
                      id="codigo-otp-erro"
                      role="alert"
                      className="text-xs font-medium text-destructive"
                    >
                      {erroCampoCodigo}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Já usa um autenticador na PDPJ? Informe o código atual. Senão,
                      deixe em branco.
                    </p>
                  )}
                </div>

                <Button type="submit" className="w-full gap-2" disabled={conectando}>
                  <Link2 className="h-4 w-4" />
                  Conectar-se à PDPJ
                </Button>
              </form>
            )}

            {/* (b) Imagem do QR Code de um autenticador que já existe. */}
            {metodo === 'imagem' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Já usa um aplicativo autenticador (Google Authenticator, Authy,
                  etc.) com a PDPJ? Envie a imagem do QR Code. Decodificamos com
                  segurança — a imagem não fica salva.
                </p>
                <QrCodeTotpUploader compact />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
